/**
 * CRM Help page (/crm/help) — a single page with two tabs that render the two
 * repo-versioned handbooks (Recruiter Quick-start, Admin guide) as markdown.
 *
 * Both tabs are always selectable by every CRM user. `useIsCRMAdmin` ONLY
 * seeds the default tab on a first visit (admins -> Admin guide, recruiters ->
 * Recruiter Quick-start); after that the last-selected tab is restored from
 * localStorage (crm.help.tab). Content is static, client-side, repo-versioned
 * documentation — nothing is fetched from crm.*, no patient data is read.
 */
import { useState } from 'react';
import MarkdownText from '../components/MarkdownText';
import { getPref, setPref, useIsCRMAdmin } from '../lib';
import recruiterQuickStart from '../help/recruiterQuickStart';
import adminGuide from '../help/adminGuide';

type HelpTab = 'recruiter' | 'admin';

const TABS: { key: HelpTab; label: string }[] = [
  { key: 'recruiter', label: 'Recruiter quick-start' },
  { key: 'admin', label: 'Admin guide' },
];

export default function Help() {
  const isAdmin = useIsCRMAdmin();
  // getPref default seeds the role-appropriate tab on the first visit; later
  // visits restore the user's last choice. Guard against a stale/garbage value.
  const [tab, setTab] = useState<HelpTab>(() => {
    const stored = getPref('help.tab', isAdmin ? 'admin' : 'recruiter');
    return stored === 'admin' ? 'admin' : 'recruiter';
  });

  const selectTab = (next: HelpTab) => {
    setTab(next);
    setPref('help.tab', next);
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Help</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Handbooks for using the Lead CRM. Both tabs are available to everyone.
        </p>
      </div>

      {/* Two-tab switcher — primary active treatment, 44px tap targets. */}
      <div role="tablist" aria-label="Help handbooks" className="mb-6 flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectTab(t.key)}
              className={`-mb-px inline-flex min-h-[44px] items-center px-4 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                active
                  ? 'border-b-2 border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <MarkdownText text={tab === 'admin' ? adminGuide : recruiterQuickStart} />
    </div>
  );
}
